import { useEffect, useState } from "react";
import { ChevronRight } from "lucide-react";
import { Button, ErrorNote, Field, SidePanel, inputClass, useCanWrite, useIsAdmin } from "@/components/ui";
import { DeleteForever } from "@/components/delete";
import { FusoLetto, SiblingProposalPanel, SymbolPanel, SymbolsTable, symbolStatus, usePeersOf } from "@/components/symbols";
import { ApiError, api, errorMessage } from "@/lib/api";
import { useRefreshDirectory, useServers } from "@/lib/queries";
import type { BrokerServer, ServerKind, SymbolMapping } from "@/lib/types";

/*
 * I SERVER, VISTI DAL LORO POSSESSORE.
 *
 * Un server MT5 è di una prop firm o di un broker (`BrokerServer.propFirmId`):
 * si guarda dalla pagina di chi lo possiede, non da un elenco a parte. Questo
 * file è la parte che le due pagine (Prop firm, Broker) hanno in comune.
 */

/** I server divisi per possessore, e quelli nati senza (dal pannello «Nuovo conto»). */
export function useServersByOwner(kind: ServerKind) {
  const servers = useServers();
  const ofKind = (servers.data ?? []).filter((s) => s.kind === kind);
  const byOwner = new Map<string, BrokerServer[]>();
  const orphans: BrokerServer[] = [];
  for (const s of ofKind) {
    if (!s.propFirmId) orphans.push(s);
    else byOwner.set(s.propFirmId, [...(byOwner.get(s.propFirmId) ?? []), s]);
  }
  /** Finché non sono letti, i server non sono «zero»: sono sconosciuti. */
  const ready = !servers.isLoading && !servers.error;
  return { byOwner, orphans, ready, error: servers.error };
}

/** «3 server · 5 conti», per la riga del possessore chiuso. */
export function serversSummary(servers: BrokerServer[]): string {
  const conti = servers.reduce((n, s) => n + s._count.accounts, 0);
  const s = servers.length === 1 ? "1 server" : `${servers.length} server`;
  return `${s} · ${conti} ${conti === 1 ? "conto" : "conti"}`;
}

/** Il pezzo cliccabile dell'intestazione di un possessore: apre e chiude. */
export function OwnerToggle({
  open,
  onToggle,
  title,
  summary,
}: {
  open: boolean;
  onToggle: () => void;
  title: string;
  summary: string;
}) {
  return (
    <button type="button" onClick={onToggle} aria-expanded={open} className="flex min-w-0 flex-1 items-center gap-2 text-left">
      <ChevronRight className={`h-4 w-4 shrink-0 text-faint transition-transform ${open ? "rotate-90" : ""}`} />
      <span className="min-w-0">
        <span className="block font-medium text-ink">{title}</span>
        <span className="block text-xs text-faint">{summary}</span>
      </span>
    </button>
  );
}

/** I server di UN possessore, con «Aggiungi server» che nasce legato a lui. */
export function OwnerServers({
  ownerId,
  ownerName,
  kind,
  servers,
  error,
}: {
  ownerId: string;
  ownerName: string;
  kind: ServerKind;
  /** `null` = non ancora letti: non si dice «nessun server». */
  servers: BrokerServer[] | null;
  error?: unknown;
}) {
  const canWrite = useCanWrite();
  const [adding, setAdding] = useState(false);

  return (
    <div className="border-t border-line">
      <div className="flex items-center justify-between gap-3 px-4 py-2">
        <h3 className="text-xs font-medium uppercase tracking-wide text-faint">Server</h3>
        {canWrite && (
          <Button size="sm" onClick={() => setAdding(true)}>
            Aggiungi server
          </Button>
        )}
      </div>
      {servers === null ? (
        error ? (
          <div className="px-4 pb-4">
            <ErrorNote>{errorMessage(error)}</ErrorNote>
          </div>
        ) : (
          <p className="px-4 pb-4 text-sm text-muted">Caricamento…</p>
        )
      ) : servers.length === 0 ? (
        <p className="px-4 pb-4 text-sm text-muted">
          Nessun server di {ownerName}. Aggiungi quello del conto: si verifica sul gateway prima di salvarlo, e nasce legato a{" "}
          {kind === "PROP" ? "questa firm" : "questo broker"}.
        </p>
      ) : (
        <ServerRows servers={servers} withSiblings />
      )}
      {adding && <AddServerPanel ownerId={ownerId} ownerName={ownerName} kind={kind} onClose={() => setAdding(false)} />}
    </div>
  );
}

/** I server nati senza possessore: non spariscono con la pagina Server. */
export function OrphanServers({ kind, servers }: { kind: ServerKind; servers: BrokerServer[] }) {
  const [open, setOpen] = useState(false);
  if (servers.length === 0) return null;
  const isProp = kind === "PROP";
  return (
    <section className="card">
      <header className="flex items-center justify-between gap-3 px-4 py-3">
        <OwnerToggle
          open={open}
          onToggle={() => setOpen((o) => !o)}
          title={isProp ? "Server senza prop firm" : "Server senza broker"}
          summary={serversSummary(servers)}
        />
      </header>
      {open && (
        <>
          <p className="border-t border-line px-4 py-2 text-xs text-faint">
            Nati dal pannello «Nuovo conto» senza indicare {isProp ? "la prop firm" : "il broker"}. I conti che li usano
            funzionano come gli altri; non hanno un possessore a cui proporre la mappatura di un server fratello.
          </p>
          <ServerRows servers={servers} withSiblings={false} />
        </>
      )}
    </section>
  );
}

/** Le righe dei server, con i pannelli dei simboli che si aprono da lì. */
function ServerRows({ servers, withSiblings }: { servers: BrokerServer[]; withSiblings: boolean }) {
  const canWrite = useCanWrite();
  const isAdmin = useIsAdmin();
  const peersOf = usePeersOf();
  const [symbolPanel, setSymbolPanel] = useState<{ server: BrokerServer; symbol: SymbolMapping | null; canonical?: string } | null>(null);
  const [proposal, setProposal] = useState<BrokerServer | null>(null);

  // I fratelli sono i server dello stesso possessore che hanno già qualcosa da proporre.
  const siblingsOf = (srv: BrokerServer) => (withSiblings ? servers.filter((s) => s.id !== srv.id && s.symbols.length > 0) : []);

  return (
    <>
      {servers.map((srv) => (
        <ServerBlock
          key={srv.id}
          server={srv}
          canWrite={canWrite}
          isAdmin={isAdmin}
          peersOf={peersOf}
          hasSiblings={siblingsOf(srv).length > 0}
          onAdd={(canonical) => setSymbolPanel({ server: srv, symbol: null, canonical })}
          onEdit={(symbol) => setSymbolPanel({ server: srv, symbol })}
          onPropose={() => setProposal(srv)}
        />
      ))}
      {symbolPanel && (
        <SymbolPanel
          server={symbolPanel.server}
          symbol={symbolPanel.symbol}
          canonical={symbolPanel.canonical}
          peersOf={peersOf}
          onClose={() => setSymbolPanel(null)}
        />
      )}
      {proposal && <SiblingProposalPanel server={proposal} siblings={siblingsOf(proposal)} onClose={() => setProposal(null)} />}
    </>
  );
}

function ServerBlock({
  server,
  canWrite,
  isAdmin,
  peersOf,
  hasSiblings,
  onAdd,
  onEdit,
  onPropose,
}: {
  server: BrokerServer;
  canWrite: boolean;
  isAdmin: boolean;
  peersOf: (serverId: string, canonical: string) => string[];
  hasSiblings: boolean;
  onAdd: (canonical?: string) => void;
  onEdit: (s: SymbolMapping) => void;
  onPropose: () => void;
}) {
  // L'elenco dei simboli non si vede finché non lo si apre.
  const [open, setOpen] = useState(false);

  return (
    <div className="border-t border-line">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 pt-3">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h4 className="text-sm font-semibold text-ink">{server.displayName ?? server.name}</h4>
          <FusoLetto server={server} />
          <span className="text-sm text-faint">
            {server._count.accounts} {server._count.accounts === 1 ? "conto" : "conti"}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
            {open ? "Nascondi i simboli" : "Vedi i simboli"}
          </Button>
          {/*
           * Solo ADMIN: un server nato per errore si toglie di mezzo
           * insieme alle sue mappature, e non e' un gesto da lasciare a chi
           * lo aggiunge tutti i giorni.
           */}
          {isAdmin && <DeleteForever kind="server" id={server.id} label={server.displayName ?? server.name} size="sm" />}
        </div>
      </div>
      <p className="px-4 pb-3 pt-1 text-xs text-muted">{symbolStatus(server)}</p>
      {open && (
        <SymbolsTable server={server} canWrite={canWrite} peersOf={peersOf} hasSiblings={hasSiblings} onAdd={onAdd} onEdit={onEdit} onPropose={onPropose} />
      )}
    </div>
  );
}

/**
 * Un server nuovo, legato a chi lo possiede.
 *
 * Il tipo lo dà la pagina e non si sceglie, e il possessore nemmeno: nasce già
 * suo. La verifica sul gateway è del backend (`POST /servers`, senza
 * credenziali): se il nome non c'è, si propongono i più simili.
 */
function AddServerPanel({ ownerId, ownerName, kind, onClose }: { ownerId: string; ownerName: string; kind: ServerKind; onClose: () => void }) {
  const refresh = useRefreshDirectory();
  const [name, setName] = useState("");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName("");
    setSuggestions([]);
    setError(null);
  }, [ownerId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSuggestions([]);
    try {
      await api.servers.create({ name: name.trim(), kind, propFirmId: ownerId });
      await refresh();
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === "SERVER_NOT_ON_GATEWAY") setSuggestions(err.suggestions ?? []);
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SidePanel
      open
      onClose={onClose}
      title="Aggiungi server"
      subtitle={`${ownerName} · ${kind === "PROP" ? "server prop" : "server broker"}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button variant="primary" type="submit" form="owner-server-form" disabled={busy}>
            {busy ? "Verifica…" : "Verifica e aggiungi"}
          </Button>
        </>
      }
    >
      <form id="owner-server-form" onSubmit={submit} className="space-y-4">
        <Field label="Nome esatto del server MT5" hint="Come compare nel terminale, per esempio FundedNext-Server 2. Si verifica col gateway prima di salvare.">
          {(id) => (
            <input
              id={id}
              required
              autoFocus
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setSuggestions([]);
              }}
              className={inputClass}
            />
          )}
        </Field>
        {suggestions.length > 0 && (
          <div className="space-y-1">
            <p className="text-xs text-muted">Non trovato. Forse uno di questi:</p>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  className="rounded border border-line px-2 py-1 text-xs text-ink hover:bg-raised"
                  onClick={() => {
                    setName(s);
                    setSuggestions([]);
                    setError(null);
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        <ErrorNote>{error}</ErrorNote>
      </form>
    </SidePanel>
  );
}
